import { DataSet } from "vis-data";
import { IdType, TimelineGroup, TimelineItem } from "vis-timeline";
import {
  createContext,
  useCallback,
  useContext,
  PropsWithChildren,
  useState,
} from "react";
import {
  ANNOTATION_TYPES,
  HAND_IDS,
  PREDICTION_TYPES,
} from "@/entities/annotation/model/consts";
import { TIMELINE_ITEM_VIEW } from "@/features/annotation/lib/consts";
import {
  TIMELINE_ITEM_COLORS,
  ZONE_BASED_PREDICTIONS_GROUP,
} from "../lib/consts";
import {
  PinBasedPrediction,
  TimelineItemColor,
  ZoneBasedPrediction,
} from "../lib/types";
import { displayUUID } from "@/shared";
import { ProximityTimeRange } from "@/shared/api/lod-api";
import { getHandSide } from "../lib/helpers";
import dayjs from "dayjs";

export interface CustomTimelineItem extends TimelineItem {
  view?: string;
  color?: TimelineItemColor;
  parentGroup?: IdType;
  childGroup?: IdType;
  fetchedAt?: Date;
  isOutsideRange?: boolean;
  systemTime?: string;
}

export interface CustomTimelineGroup extends TimelineGroup {
  color?: TimelineItemColor;
  isHidden?: boolean;
  isSaved?: boolean;
  canHide?: boolean;
  canSave?: boolean;
  parentGroup?: IdType;
  animalName?: string;
  animalColor?: string;
  trackId?: number;
}

export interface TimelineEventBase {
  id: string;
  journeyId: string;
  timestamp: string;
  type: string;
}

interface GondolaChangeTimelineItem {
  id: string;
  camera_local_id: string;
  frame_start_time: string;
  frame_end_time: string;
  gondola_name: string;
  gondola_id: number;
}

interface IntersectedProxTimelineItem {
  id: string;
  track_id: number;
  time_start: string;
  time_finish: string;
  hand_id: number;
  gondola_name: string;
  gondola_id: number;
}

interface TimelineItemUpdate {
  id: IdType;
  color: TimelineItemColor;
}

interface TimelineDataContext {
  dataset: DataSet<CustomTimelineItem>;
  groups: DataSet<CustomTimelineGroup>;
  showSavedOnly: boolean;
  setShowSavedOnly: (showSavedOnly: boolean) => void;
  setTimelineBackgroundRanges: (startTime: Date, endTime: Date) => void;
  updateTimelineBackgroundRanges: (startTime: Date, endTime: Date) => void;
  addTimelineOrderEvent: (orderId: string, orderTime: Date) => void;
  addTimelineSystemTimeEvent: (
    selectedOrderId: string,
    dateRangeStartTime: Date,
    dateRangeEndTime: Date | null,
    systemDateTime: string,
    fetchedTime: string,
    isDelayed?: boolean,
    isAfterRange?: boolean,
    isBeforeRange?: boolean
  ) => void;
  createOrderTimelineGroup: (orderId: string) => void;
  addTimelineCustomerJourneyEvent: (event: TimelineEventBase) => void;
  addTimelineCustomerJourneyEvents: (events: TimelineEventBase[]) => void;
  createJourneyTimelineGroups: (
    journeyId: string,
    hasTransactions?: boolean,
    hasTrackPersistences?: boolean,
    hasJourneyProximities?: boolean,
    hasLeftHandProximityTimeRanges?: boolean,
    hasRightHandProximityTimeRanges?: boolean
  ) => void;
  createPinPredictionTimelineGroups: (
    pinBasedPrediction: PinBasedPrediction[]
  ) => void;
  createZonePredictionTimelineGroups: (
    zoneBasedPrediction: ZoneBasedPrediction[]
  ) => void;
  addTimelineTransactionEvent: (transaction: TimelineEventBase) => void;
  addTimelineTrackPersistenceEvent: (
    trackPersistence: TimelineEventBase
  ) => void;
  addTimelineJourneyProximityEvent: (
    journeyProximity: TimelineEventBase
  ) => void;
  addTimelineProximityTimeRangeEvents: (
    proximityTimeRange: ProximityTimeRange
  ) => void;
  addGondolaChangesToTimeline: (
    gondolaChanges: GondolaChangeTimelineItem[],
    gondolaGroupId: string
  ) => void;
  removeGondolaChangesFromTimeline: (gondolaGroupId: string) => void;
  highlightGondolaChangesTimeline: (gondolaChangeId: string | null) => void;
  addIntersectedProxsToTimeline: (
    intersectedProxs: IntersectedProxTimelineItem[],
    gondolaGroupId: string,
    avatarBindings?: Record<
      number,
      { trackId: number; animalName: string; color: string }
    >
  ) => void;
  removeIntersectedProxsFromTimeline: (gondolaGroupId: string) => void;
  highlightIntersectedProxsTimeline: (proxId: string | null) => void;
  highlightIntersectedProxsByMatchScores: (
    proxIds: string[],
    positiveScoreIndices: number[]
  ) => void;
  clearIntersectedProxsHighlighting: () => void;
  addSelectedProximityToTimeline: (
    trackId: number,
    extraData: string,
    avatarBinding?: { animalName: string; color: string }
  ) => void;
  clearSelectedProximityFromTimeline: () => void;
  removeTimelineAnnotationEvent: (event: TimelineEventBase) => void;
  removeTimelineGroup: (groupId: string) => void;
  removeTimelineItemsByGroup: (groupId: string) => void;
  removePinPredictionTimelineGroups: (
    pinBasedPredictions: PinBasedPrediction[]
  ) => void;
  removeZonePredictionTimelineGroups: (
    zoneBasedPredictions: ZoneBasedPrediction[]
  ) => void;
  toggleTimelineGroup: (groupId: string) => void;
  toggleSavedItemsVisibility: (showSavedOnly: boolean) => void;
  highlightTimelineItem: (
    selectedOrderId: string | null,
    eventId?: string | null
  ) => void;
  clearTimelineData: () => void;
}

// Combined hook for managing timeline data
function useTimelineDataManagement() {
  const [dataset] = useState<DataSet<CustomTimelineItem>>(() => new DataSet());
  const [groups] = useState<DataSet<CustomTimelineGroup>>(() => new DataSet());
  const [showSavedOnly, setShowSavedOnly] = useState<boolean>(false);

  const setTimelineBackgroundRanges = useCallback(
    (startTime: Date, endTime: Date): void => {
      dataset.clear();

      dataset.add({
        id: "START",
        content: "",
        start: new Date(0),
        end: startTime,
        type: "background",
      });

      dataset.add({
        id: "END",
        content: "",
        start: endTime,
        end: dayjs(endTime).add(1, "month").toDate(),
        type: "background",
      });
    },
    [dataset]
  );

  const updateTimelineBackgroundRanges = useCallback(
    (startTime: Date, endTime: Date): void => {
      dataset.update({
        id: "START",
        content: "",
        start: new Date(0),
        end: startTime,
        type: "background",
      });

      dataset.update({
        id: "END",
        content: "",
        start: endTime,
        end: dayjs(endTime).add(1, "month").toDate(),
        type: "background",
      });
    },
    [dataset]
  );

  const addTimelineOrderEvent = useCallback(
    (orderId: string, orderTime: Date) => {
      dataset.update({
        id: orderId,
        start: orderTime,
        content: "order",
        view: TIMELINE_ITEM_VIEW.EVENT,
        className: TIMELINE_ITEM_VIEW.EVENT,
        color: TIMELINE_ITEM_COLORS.PURPLE,
        group: orderId,
      });
    },
    [dataset]
  );

  const addTimelineSystemTimeEvent = useCallback(
    (
      selectedOrderId: string,
      dateRangeStartTime: Date,
      dateRangeEndTime: Date | null,
      systemDateTime: string,
      fetchedTime: string,
      isDelayed = false,
      isAfterRange = false,
      isBeforeRange = false
    ) => {
      const isBarExists = !!dataset.get("system-time-event");
      const isCircleExists = !!dataset.get("system-time-circle-event");

      if (isBeforeRange) {
        if (isBarExists) {
          dataset.remove("system-time-event");
        }

        if (isCircleExists) {
          dataset.remove("system-time-circle-event");
        }

        return;
      }

      if (isAfterRange && isCircleExists) {
        dataset.remove("system-time-circle-event");
      }

      const isWithinRange = !isAfterRange && !isBeforeRange;
      const systemTime = new Date(systemDateTime);
      const timelineColor = isAfterRange
        ? TIMELINE_ITEM_COLORS.TEAL
        : isDelayed
          ? TIMELINE_ITEM_COLORS.RED
          : TIMELINE_ITEM_COLORS.TEAL;

      const classNames = [
        TIMELINE_ITEM_VIEW.SYSTEM_TIME,
        isDelayed && "system-time-delayed",
        isAfterRange && "system-time-outside-range",
      ]
        .filter(Boolean)
        .join(" ");

      dataset.update({
        id: "system-time-event",
        start: dateRangeStartTime,
        end: isAfterRange && dateRangeEndTime ? dateRangeEndTime : systemTime,
        content: `System Time: ${systemDateTime}`,
        view: TIMELINE_ITEM_VIEW.SYSTEM_TIME,
        className: classNames,
        color: timelineColor,
        group: selectedOrderId,
        systemTime: systemDateTime,
        fetchedAt: new Date(fetchedTime),
        isOutsideRange: isAfterRange,
      });

      if (isWithinRange) {
        dataset.update({
          id: "system-time-circle-event",
          start: systemTime,
          content: `System Time: ${systemDateTime}`,
          view: TIMELINE_ITEM_VIEW.SYSTEM_TIME_CIRCLE,
          className: TIMELINE_ITEM_VIEW.SYSTEM_TIME_CIRCLE,
          color: timelineColor,
          group: selectedOrderId,
          systemTime: systemDateTime,
          fetchedAt: new Date(fetchedTime),
        });
      }
    },
    [dataset]
  );

  const addTimelineCustomerJourneyEvent = useCallback(
    (event: TimelineEventBase) => {
      const existingGroup = groups.get(event.journeyId);
      const currentNestedGroups = existingGroup?.nestedGroups || [];

      const journeyEventGroupId = `${event.journeyId}-${ANNOTATION_TYPES.JOURNEY}`;
      const updatedNestedGroups = currentNestedGroups.includes(
        journeyEventGroupId
      )
        ? currentNestedGroups
        : [...currentNestedGroups, journeyEventGroupId];

      groups.update({
        id: event.journeyId,
        content: event.journeyId && displayUUID(event.journeyId),
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: false,
        canSave: true,
        nestedGroups: updatedNestedGroups,
      });

      groups.update({
        id: `${event.journeyId}-${ANNOTATION_TYPES.JOURNEY}`,
        content: "Journey Annotation",
        canHide: false,
        canSave: true,
        parentGroup: event.journeyId,
      });

      dataset.update({
        id: event.id,
        start: event.timestamp,
        content: event.id,
        view: TIMELINE_ITEM_VIEW.EVENT,
        className: TIMELINE_ITEM_VIEW.EVENT,
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        group: `${event.journeyId}-${ANNOTATION_TYPES.JOURNEY}`,
        parentGroup: event.journeyId,
        childGroup: `${event.journeyId}-${ANNOTATION_TYPES.JOURNEY}`,
      });
    },
    []
  );

  const addTimelineCustomerJourneyEvents = useCallback(
    (events: TimelineEventBase[]) => {
      if (!events.length) return;

      const eventsByJourney = events.reduce(
        (acc, event) => {
          if (!acc[event.journeyId]) {
            acc[event.journeyId] = [];
          }
          acc[event.journeyId].push(event);
          return acc;
        },
        {} as Record<string, TimelineEventBase[]>
      );

      const groupUpdates: CustomTimelineGroup[] = [];
      const datasetUpdates: CustomTimelineItem[] = [];

      Object.entries(eventsByJourney).forEach(([journeyId, journeyEvents]) => {
        const existingGroup = groups.get(journeyId);
        const currentNestedGroups = existingGroup?.nestedGroups || [];

        const journeyEventGroupId = `${journeyId}-${ANNOTATION_TYPES.JOURNEY}`;
        const updatedNestedGroups = currentNestedGroups.includes(
          journeyEventGroupId
        )
          ? currentNestedGroups
          : [...currentNestedGroups, journeyEventGroupId];

        groupUpdates.push({
          id: journeyId,
          content: displayUUID(journeyId) || journeyId,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          canHide: false,
          canSave: true,
          nestedGroups: updatedNestedGroups,
        });

        groupUpdates.push({
          id: journeyEventGroupId,
          content: "Journey Annotation",
          canHide: false,
          canSave: true,
          parentGroup: journeyId,
        });

        journeyEvents.forEach((event) => {
          datasetUpdates.push({
            id: event.id,
            start: event.timestamp,
            content: event.id,
            view: TIMELINE_ITEM_VIEW.EVENT,
            className: TIMELINE_ITEM_VIEW.EVENT,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: journeyEventGroupId,
            parentGroup: journeyId,
            childGroup: journeyEventGroupId,
          });
        });
      });

      if (groupUpdates.length > 0) {
        groups.update(groupUpdates);
      }
      if (datasetUpdates.length > 0) {
        dataset.update(datasetUpdates);
      }
    },
    [dataset, groups]
  );

  const addTimelineTransactionEvent = useCallback(
    (transaction: TimelineEventBase) => {
      const existingGroup = groups.get(transaction.journeyId);
      const currentNestedGroups = existingGroup?.nestedGroups || [];

      const transactionGroupId = `${transaction.journeyId}-${ANNOTATION_TYPES.TRANSACTION}`;
      const updatedNestedGroups = currentNestedGroups.includes(
        transactionGroupId
      )
        ? currentNestedGroups
        : [...currentNestedGroups, transactionGroupId];

      groups.update({
        id: transaction.journeyId,
        content: displayUUID(transaction.journeyId),
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: false,
        canSave: true,
        nestedGroups: updatedNestedGroups,
      });

      groups.update({
        id: transactionGroupId,
        content: "Transaction Annotation",
        canHide: false,
        canSave: true,
        parentGroup: transaction.journeyId,
      });

      dataset.update({
        id: transaction.id,
        start: transaction.timestamp,
        content: transaction.id,
        view: TIMELINE_ITEM_VIEW.EVENT,
        className: TIMELINE_ITEM_VIEW.EVENT,
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        group: transactionGroupId,
        parentGroup: transaction.journeyId,
        childGroup: transactionGroupId,
      });
    },
    []
  );

  const addTimelineTrackPersistenceEvent = useCallback(
    (trackPersistence: TimelineEventBase) => {
      const existingGroup = groups.get(trackPersistence.journeyId);
      const currentNestedGroups = existingGroup?.nestedGroups || [];

      const trackPersistenceGroupId = `${trackPersistence.journeyId}-${ANNOTATION_TYPES.TRACK_PERSISTENCE}`;
      const updatedNestedGroups = currentNestedGroups.includes(
        trackPersistenceGroupId
      )
        ? currentNestedGroups
        : [...currentNestedGroups, trackPersistenceGroupId];

      groups.update({
        id: trackPersistence.journeyId,
        content: displayUUID(trackPersistence.journeyId),
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: false,
        canSave: true,
        nestedGroups: updatedNestedGroups,
      });

      groups.update({
        id: trackPersistenceGroupId,
        content: "Track Persistence Annotation",
        canHide: false,
        canSave: true,
        parentGroup: trackPersistence.journeyId,
      });

      dataset.update({
        id: trackPersistence.id,
        start: trackPersistence.timestamp,
        content: trackPersistence.id,
        view: TIMELINE_ITEM_VIEW.EVENT,
        className: TIMELINE_ITEM_VIEW.EVENT,
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        group: trackPersistenceGroupId,
        parentGroup: trackPersistence.journeyId,
        childGroup: trackPersistenceGroupId,
      });
    },
    []
  );

  const addTimelineJourneyProximityEvent = useCallback(
    (journeyProximity: TimelineEventBase) => {
      const existingGroup = groups.get(journeyProximity.journeyId);
      const currentNestedGroups = existingGroup?.nestedGroups || [];

      const journeyProximityGroupId = `${journeyProximity.journeyId}-${ANNOTATION_TYPES.PROXIMITY}`;
      const updatedNestedGroups = currentNestedGroups.includes(
        journeyProximityGroupId
      )
        ? currentNestedGroups
        : [...currentNestedGroups, journeyProximityGroupId];

      groups.update({
        id: journeyProximity.journeyId,
        content: displayUUID(journeyProximity.journeyId),
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: false,
        canSave: true,
        nestedGroups: updatedNestedGroups,
      });

      groups.update({
        id: journeyProximityGroupId,
        content: "Proximity Annotation",
        canHide: false,
        canSave: true,
        parentGroup: journeyProximity.journeyId,
      });

      dataset.update({
        id: journeyProximity.id,
        start: journeyProximity.timestamp,
        content: journeyProximity.id,
        view: TIMELINE_ITEM_VIEW.EVENT,
        className: TIMELINE_ITEM_VIEW.EVENT,
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        group: journeyProximityGroupId,
        parentGroup: journeyProximity.journeyId,
        childGroup: journeyProximityGroupId,
      });
    },
    []
  );

  const addTimelineProximityTimeRangeEvents = useCallback(
    (proximityTimeRange: ProximityTimeRange) => {
      const existingGroup = groups.get(proximityTimeRange.journeyId);
      const currentNestedGroups = existingGroup?.nestedGroups || [];

      const proximityTimeRangeGroupId = `${proximityTimeRange.journeyId}-${ANNOTATION_TYPES.PROXIMITY_TIME_RANGES}-${proximityTimeRange.handId}`;
      const updatedNestedGroups = currentNestedGroups.includes(
        proximityTimeRangeGroupId
      )
        ? currentNestedGroups
        : [...currentNestedGroups, proximityTimeRangeGroupId];

      groups.update({
        id: proximityTimeRange.journeyId,
        content: displayUUID(proximityTimeRange.journeyId),
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: false,
        canSave: true,
        nestedGroups: updatedNestedGroups,
      });

      groups.update({
        id: proximityTimeRangeGroupId,
        content: `Proximity Time Ranges (${getHandSide(proximityTimeRange.handId)} hand)`,
        canHide: false,
        canSave: true,
        parentGroup: proximityTimeRange.journeyId,
      });

      const itemsToAdd = [
        {
          suffix: "",
          start: proximityTimeRange.timestamp,
        },
        {
          suffix: "_before-start",
          start: proximityTimeRange.beforeRangeStartTime,
        },
        {
          suffix: "_before-end",
          start: proximityTimeRange.beforeRangeEndTime,
        },
        {
          suffix: "_after-start",
          start: proximityTimeRange.afterRangeStartTime,
        },
        {
          suffix: "_after-end",
          start: proximityTimeRange.afterRangeEndTime,
        },
        {
          suffix: "_range",
          start: proximityTimeRange.beforeRangeStartTime,
          end: proximityTimeRange.afterRangeEndTime,
          view: TIMELINE_ITEM_VIEW.EVENT_RANGE,
          className: TIMELINE_ITEM_VIEW.EVENT_RANGE,
        },
      ];

      itemsToAdd.forEach(({ suffix, start, end, view, className }) => {
        dataset.update({
          id: `${proximityTimeRange.id}${suffix}`,
          start,
          ...(end && { end }),
          content: proximityTimeRange.id,
          view: view || TIMELINE_ITEM_VIEW.EVENT,
          className: className || TIMELINE_ITEM_VIEW.EVENT,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          group: proximityTimeRangeGroupId,
          parentGroup: proximityTimeRange.journeyId,
          childGroup: proximityTimeRangeGroupId,
        });
      });
    },
    []
  );

  const createOrderTimelineGroup = useCallback((orderId: string) => {
    groups.update({
      id: orderId,
      content: "order creation time",
      isSaved: true,
      order: 0,
    });
  }, []);

  const createJourneyTimelineGroups = useCallback(
    (
      journeyId: string,
      hasTransactions = false,
      hasTrackPersistences = false,
      hasJourneyProximities = false,
      hasLeftHandProximityTimeRanges = false,
      hasRightHandProximityTimeRanges = false
    ) => {
      const journeyAnnotationId = `${journeyId}-${ANNOTATION_TYPES.JOURNEY}`;
      const transactionAnnotationId = `${journeyId}-${ANNOTATION_TYPES.TRANSACTION}`;
      const trackPersistenceAnnotationId = `${journeyId}-${ANNOTATION_TYPES.TRACK_PERSISTENCE}`;
      const journeyProximityAnnotationId = `${journeyId}-${ANNOTATION_TYPES.PROXIMITY}`;
      const leftHandProximityTimeRangeAnnotationId = `${journeyId}-${ANNOTATION_TYPES.PROXIMITY_TIME_RANGES}-${HAND_IDS.LEFT}`;
      const rightHandProximityTimeRangeAnnotationId = `${journeyId}-${ANNOTATION_TYPES.PROXIMITY_TIME_RANGES}-${HAND_IDS.RIGHT}`;

      const nestedGroups = [
        journeyAnnotationId,
        hasTransactions && transactionAnnotationId,
        hasTrackPersistences && trackPersistenceAnnotationId,
        hasJourneyProximities && journeyProximityAnnotationId,
        hasLeftHandProximityTimeRanges &&
          leftHandProximityTimeRangeAnnotationId,
        hasRightHandProximityTimeRanges &&
          rightHandProximityTimeRangeAnnotationId,
      ].filter(Boolean) as string[];

      groups.update({
        id: journeyId,
        content: displayUUID(journeyId),
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: false,
        canSave: true,
        nestedGroups,
      });

      groups.update({
        id: journeyAnnotationId,
        content: "Journey Annotation",
        canHide: false,
        canSave: true,
        parentGroup: journeyId,
      });

      if (hasTransactions) {
        groups.update({
          id: transactionAnnotationId,
          content: "Transaction Annotation",
          canHide: false,
          canSave: true,
          parentGroup: journeyId,
        });
      }

      if (hasTrackPersistences) {
        groups.update({
          id: trackPersistenceAnnotationId,
          content: "Track Persistence Annotation",
          canHide: false,
          canSave: true,
          parentGroup: journeyId,
        });
      }

      if (hasJourneyProximities) {
        groups.update({
          id: journeyProximityAnnotationId,
          content: "Proximity Annotation",
          canHide: false,
          canSave: true,
          parentGroup: journeyId,
        });
      }

      if (hasLeftHandProximityTimeRanges) {
        groups.update({
          id: leftHandProximityTimeRangeAnnotationId,
          content: `Proximity Time Ranges (Left hand)`,
          canHide: false,
          canSave: true,
          parentGroup: journeyId,
        });
      }

      if (hasRightHandProximityTimeRanges) {
        groups.update({
          id: rightHandProximityTimeRangeAnnotationId,
          content: `Proximity Time Ranges (Right hand)`,
          canHide: false,
          canSave: true,
          parentGroup: journeyId,
        });
      }
    },
    []
  );

  const createPinPredictionTimelineGroups = useCallback(
    (pinBasedPredictions: PinBasedPrediction[]) => {
      pinBasedPredictions.forEach((pinBasedPrediction) => {
        const existingGroup = groups.get(pinBasedPrediction.journeyId);
        const currentNestedGroups = existingGroup?.nestedGroups || [];

        const pinBasedPredictionGroupId = `${pinBasedPrediction.journeyId}-${PREDICTION_TYPES.PIN}`;
        const updatedNestedGroups = currentNestedGroups.includes(
          pinBasedPredictionGroupId
        )
          ? currentNestedGroups
          : [...currentNestedGroups, pinBasedPredictionGroupId];

        groups.update({
          id: pinBasedPrediction.journeyId,
          content: displayUUID(pinBasedPrediction.journeyId),
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          canHide: false,
          canSave: true,
          nestedGroups: updatedNestedGroups,
        });

        groups.update({
          id: pinBasedPredictionGroupId,
          content: "Pin Based Predictions",
          canHide: false,
          canSave: true,
          parentGroup: pinBasedPrediction.journeyId,
        });

        dataset.update({
          id: pinBasedPrediction.id,
          start: pinBasedPrediction.timestamp,
          content: pinBasedPrediction.id,
          view: TIMELINE_ITEM_VIEW.EVENT,
          className: TIMELINE_ITEM_VIEW.EVENT,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          group: pinBasedPredictionGroupId,
          parentGroup: pinBasedPrediction.journeyId,
          childGroup: pinBasedPredictionGroupId,
        });
      });
    },
    []
  );

  const createZonePredictionTimelineGroups = useCallback(
    (zoneBasedPredictions: ZoneBasedPrediction[]) => {
      zoneBasedPredictions.forEach((zoneBasedPrediction) => {
        const existingGroup = groups.get(ZONE_BASED_PREDICTIONS_GROUP);
        const currentNestedGroups = existingGroup?.nestedGroups || [];

        const zoneBasedPredictionGroupId = zoneBasedPrediction.trackId;
        const updatedNestedGroups = currentNestedGroups.includes(
          zoneBasedPredictionGroupId
        )
          ? currentNestedGroups
          : [...currentNestedGroups, zoneBasedPredictionGroupId];

        groups.update({
          id: ZONE_BASED_PREDICTIONS_GROUP,
          content: ZONE_BASED_PREDICTIONS_GROUP,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          canHide: false,
          canSave: true,
          nestedGroups: updatedNestedGroups,
        });

        groups.update({
          id: zoneBasedPredictionGroupId,
          content: `TrackID-${zoneBasedPredictionGroupId}`,
          canHide: false,
          canSave: true,
          parentGroup: ZONE_BASED_PREDICTIONS_GROUP,
        });

        dataset.update({
          id: zoneBasedPrediction.id,
          start: zoneBasedPrediction.timestamp,
          content: zoneBasedPrediction.id,
          view: TIMELINE_ITEM_VIEW.EVENT,
          className: TIMELINE_ITEM_VIEW.EVENT,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          group: zoneBasedPrediction.trackId,
          parentGroup: ZONE_BASED_PREDICTIONS_GROUP,
          childGroup: zoneBasedPrediction.trackId,
        });
      });
    },
    []
  );

  const removePinPredictionTimelineGroups = useCallback(
    (pinBasedPredictions: PinBasedPrediction[]) => {
      pinBasedPredictions.forEach((pinBasedPrediction) => {
        dataset.remove(pinBasedPrediction.id);

        const pinBasedPredictionGroupId = `${pinBasedPrediction.journeyId}-${PREDICTION_TYPES.PIN}`;

        const existingGroup = groups.get(pinBasedPrediction.journeyId);

        if (existingGroup?.nestedGroups) {
          const updatedNestedGroups = existingGroup.nestedGroups.filter(
            (groupId) => groupId !== pinBasedPredictionGroupId
          );

          if (updatedNestedGroups.length > 0) {
            groups.update({
              ...existingGroup,
              nestedGroups: updatedNestedGroups,
            });
          } else {
            groups.remove(pinBasedPrediction.journeyId);
          }
        }

        groups.remove(pinBasedPredictionGroupId);
      });
    },
    []
  );

  const removeZonePredictionTimelineGroups = useCallback(
    (zoneBasedPredictions: ZoneBasedPrediction[]) => {
      zoneBasedPredictions.forEach((zoneBasedPrediction) => {
        dataset.remove(zoneBasedPrediction.id);

        const zoneBasedPredictionGroupId = zoneBasedPrediction.trackId;

        const existingGroup = groups.get(ZONE_BASED_PREDICTIONS_GROUP);

        if (existingGroup?.nestedGroups) {
          const updatedNestedGroups = existingGroup.nestedGroups.filter(
            (groupId) => groupId !== zoneBasedPredictionGroupId
          );

          if (updatedNestedGroups.length > 0) {
            groups.update({
              ...existingGroup,
              nestedGroups: updatedNestedGroups,
            });
          } else {
            groups.remove(ZONE_BASED_PREDICTIONS_GROUP);
          }
        }

        groups.remove(zoneBasedPredictionGroupId);
      });
    },
    []
  );

  // Removal functions
  const removeTimelineItem = useCallback((itemId: string) => {
    dataset.remove(itemId);
  }, []);

  const removeTimelineItems = useCallback((itemId: string) => {
    const itemsToRemove = dataset.get({
      filter: (item) => item.id.toString().startsWith(itemId),
    });

    itemsToRemove.forEach(({ id }) => {
      dataset.remove(id);
    });
  }, []);

  const removeTimelineGroup = useCallback((groupId: string) => {
    groups.remove(groupId);
  }, []);

  const removeTimelineAnnotationEvent = useCallback(
    (event: TimelineEventBase) => {
      if (!event?.journeyId) return;

      const journeyGroup = groups.get(event.journeyId);
      const datasetItem = dataset.get(event.id);
      const groupType = datasetItem?.group?.toString();

      if (!journeyGroup || !groupType) return;

      if (event.type === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES) {
        removeTimelineItems(event.id);
      } else {
        removeTimelineItem(event.id);
      }

      const remainingEvents = dataset.get({
        filter: (item) => item.group === groupType && item.id !== event.id,
      });

      if (remainingEvents.length === 0) {
        const updatedNestedGroups = (journeyGroup.nestedGroups || []).filter(
          (id) => id !== groupType
        );

        if (updatedNestedGroups.length > 0) {
          groups.update({
            id: event.journeyId,
            nestedGroups: updatedNestedGroups,
          });
        }

        removeTimelineGroup(groupType);
      }
    },
    [dataset, groups]
  );

  const removeTimelineItemsByGroup = useCallback(
    (groupId: string) => {
      dataset.remove(
        dataset
          .get({
            filter: (item) => item.group === groupId,
          })
          .map((item) => item.id)
      );
    },
    [dataset]
  );

  const toggleTimelineGroup = useCallback(
    (groupId: string): void => {
      const parentGroup = groups.get(groupId);
      const isExpanded = !parentGroup?.showNested;

      if (!parentGroup?.nestedGroups?.length) {
        return;
      }

      const items = dataset.get({
        filter: (item) => item.parentGroup === groupId,
      });

      items.forEach((item) => {
        dataset.update({
          id: item.id,
          group: isExpanded ? item.childGroup : item.parentGroup,
        });
      });
    },
    [dataset, groups]
  );

  const toggleSavedItemsVisibility = (showSavedOnly: boolean) => {
    groups.forEach((group) => {
      const updatedGroup = {
        id: group.id,
        visible: group.canSave && showSavedOnly ? !!group.isSaved : true,
      };
      groups.update(updatedGroup);
    });
  };

  const highlightTimelineItem = useCallback(
    (selectedOrderId: string | null, eventId?: string | null): void => {
      const batchUpdates: TimelineItemUpdate[] = [];
      let needsUpdate = false;

      if (eventId) {
        const eventItem = dataset.get(eventId);
        if (eventItem && eventItem.color !== TIMELINE_ITEM_COLORS.RED) {
          batchUpdates.push({
            id: eventId,
            color: TIMELINE_ITEM_COLORS.RED,
          });
          needsUpdate = true;
        }
      }

      dataset.forEach((item) => {
        if (eventId && item.id === eventId) {
          return;
        }

        // Skip system time items from highlighting
        if (item.id === "system-time-event") {
          return;
        }

        const newColor =
          item.id === selectedOrderId
            ? TIMELINE_ITEM_COLORS.PURPLE
            : item.color === TIMELINE_ITEM_COLORS.RED
              ? TIMELINE_ITEM_COLORS.MEDIUM_GRAY
              : item.color;

        if (newColor !== item.color) {
          batchUpdates.push({
            id: item.id,
            color: newColor as TimelineItemColor,
          });
          needsUpdate = true;
        }
      });

      if (!needsUpdate || batchUpdates.length === 0) {
        return;
      }

      dataset.update(batchUpdates);
    },
    [dataset]
  );

  // Clear function
  const clearTimelineData = useCallback(() => {
    dataset.clear();
    groups.clear();
  }, []);

  const addGondolaChangesToTimeline = useCallback(
    (gondolaChanges: GondolaChangeTimelineItem[], gondolaGroupId: string) => {
      if (!gondolaChanges.length) return;

      // Create main group for gondola changes
      const mainGroupId = `gondola-changes-${gondolaGroupId}`;

      // Group gondola changes by camera_local_id
      const changesByCamera = gondolaChanges.reduce(
        (acc, change) => {
          if (!acc[change.camera_local_id]) {
            acc[change.camera_local_id] = [];
          }
          acc[change.camera_local_id].push(change);
          return acc;
        },
        {} as Record<string, GondolaChangeTimelineItem[]>
      );

      const cameraGroups = Object.keys(changesByCamera);
      const nestedGroups = cameraGroups.map(
        (cameraId) => `${mainGroupId}-${cameraId}`
      );

      // Create main group
      groups.update({
        id: mainGroupId,
        content: `Gondola Changes (${gondolaChanges[0].gondola_name})`,
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: true,
        canSave: false,
        order: 1,
        nestedGroups,
      });

      // Create groups for each camera and add items
      Object.entries(changesByCamera).forEach(([cameraId, changes]) => {
        const cameraGroupId = `${mainGroupId}-${cameraId}`;

        // Create camera group
        groups.update({
          id: cameraGroupId,
          content: `Camera ${cameraId}`,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          canHide: false,
          canSave: false,
          parentGroup: mainGroupId,
        });

        // Add timeline ranges for each gondola change - all using gray color
        changes.forEach((change) => {
          const startTime = new Date(change.frame_start_time);
          const endTime = new Date(change.frame_end_time);
          const baseContent = `Gondola ${change.gondola_id}`;

          dataset.update({
            id: `${change.id}-start`,
            start: startTime,
            content: `${baseContent} Start`,
            view: TIMELINE_ITEM_VIEW.EVENT,
            className: TIMELINE_ITEM_VIEW.EVENT,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: cameraGroupId,
            parentGroup: mainGroupId,
            childGroup: cameraGroupId,
          });

          dataset.update({
            id: `${change.id}-end`,
            start: endTime,
            content: `${baseContent} End`,
            view: TIMELINE_ITEM_VIEW.EVENT,
            className: TIMELINE_ITEM_VIEW.EVENT,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: cameraGroupId,
            parentGroup: mainGroupId,
            childGroup: cameraGroupId,
          });

          dataset.update({
            id: `${change.id}-range`,
            start: startTime,
            end: endTime,
            content: baseContent,
            view: TIMELINE_ITEM_VIEW.EVENT_RANGE,
            className: TIMELINE_ITEM_VIEW.EVENT_RANGE,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: cameraGroupId,
            parentGroup: mainGroupId,
            childGroup: cameraGroupId,
          });
        });
      });
    },
    [dataset, groups]
  );

  const removeGondolaChangesFromTimeline = useCallback(
    (gondolaGroupId: string) => {
      const mainGroupId = `gondola-changes-${gondolaGroupId}`;

      // Remove all items belonging to this gondola group
      const itemsToRemove = dataset.get({
        filter: (item) =>
          item.parentGroup === mainGroupId || item.group === mainGroupId,
      });

      itemsToRemove.forEach((item) => {
        dataset.remove(item.id);
      });

      // Remove all groups related to this gondola group
      const groupsToRemove = groups.get({
        filter: (group) =>
          group.id === mainGroupId || group.parentGroup === mainGroupId,
      });

      groupsToRemove.forEach((group) => {
        groups.remove(group.id);
      });
    },
    [dataset, groups]
  );

  const highlightGondolaChangesTimeline = useCallback(
    (selectedGondolaChangeId: string | null) => {
      const allGondolaItems = dataset.get({
        filter: (item) => {
          if (!item.id || typeof item.id !== "string") return false;

          return (
            item.id.includes("-start") ||
            item.id.includes("-end") ||
            item.id.includes("-range")
          );
        },
      });

      const batchUpdates: Array<{ id: any; color?: string }> = [];

      allGondolaItems.forEach((item) => {
        const itemId = item.id as string;

        const isSelected =
          selectedGondolaChangeId &&
          (itemId === `${selectedGondolaChangeId}-start` ||
            itemId === `${selectedGondolaChangeId}-end` ||
            itemId === `${selectedGondolaChangeId}-range`);

        batchUpdates.push({
          id: item.id,
          color: isSelected
            ? TIMELINE_ITEM_COLORS.BLUE
            : TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        });
      });

      if (batchUpdates.length > 0) {
        dataset.update(batchUpdates);
      }
    },
    [dataset]
  );

  const addIntersectedProxsToTimeline = useCallback(
    (
      intersectedProxs: IntersectedProxTimelineItem[],
      gondolaGroupId: string,
      avatarBindings?: Record<
        number,
        { trackId: number; animalName: string; color: string }
      >
    ) => {
      if (!intersectedProxs.length) return;

      const mainGroupId = `intersected-proxs-${gondolaGroupId}`;

      const proxsByTrack = intersectedProxs.reduce(
        (acc, prox) => {
          if (!acc[prox.track_id]) {
            acc[prox.track_id] = [];
          }
          acc[prox.track_id].push(prox);
          return acc;
        },
        {} as Record<number, IntersectedProxTimelineItem[]>
      );

      const trackGroups = Object.keys(proxsByTrack);
      const nestedGroups = trackGroups.map(
        (trackId) => `${mainGroupId}-track-${trackId}`
      );

      groups.update({
        id: mainGroupId,
        content: `Intersected Track Proxs (${intersectedProxs[0].gondola_name})`,
        color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        canHide: true,
        canSave: false,
        order: 3,
        nestedGroups,
      });

      Object.entries(proxsByTrack).forEach(([trackId, proxs]) => {
        const trackGroupId = `${mainGroupId}-track-${trackId}`;
        const trackIdNumber = parseInt(trackId);
        const avatarBinding = avatarBindings?.[trackIdNumber];

        groups.update({
          id: trackGroupId,
          content: `Track ${trackId}`,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          canHide: false,
          canSave: false,
          parentGroup: mainGroupId,
          trackId: trackIdNumber,
          animalName: avatarBinding?.animalName,
          animalColor: avatarBinding?.color,
        });

        proxs.forEach((prox) => {
          const startTime = new Date(prox.time_start);
          const endTime = new Date(prox.time_finish);
          const handSide = prox.hand_id === 0 ? "Left" : "Right";
          const baseContent = `Track ${prox.track_id} (${handSide} hand)`;

          dataset.update({
            id: `${prox.id}-start`,
            start: startTime,
            content: `${baseContent} Start`,
            view: TIMELINE_ITEM_VIEW.EVENT,
            className: TIMELINE_ITEM_VIEW.EVENT,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: trackGroupId,
            parentGroup: mainGroupId,
            childGroup: trackGroupId,
          });

          dataset.update({
            id: `${prox.id}-end`,
            start: endTime,
            content: `${baseContent} End`,
            view: TIMELINE_ITEM_VIEW.EVENT,
            className: TIMELINE_ITEM_VIEW.EVENT,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: trackGroupId,
            parentGroup: mainGroupId,
            childGroup: trackGroupId,
          });

          dataset.update({
            id: `${prox.id}-range`,
            start: startTime,
            end: endTime,
            content: baseContent,
            view: TIMELINE_ITEM_VIEW.EVENT_RANGE,
            className: TIMELINE_ITEM_VIEW.EVENT_RANGE,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: trackGroupId,
            parentGroup: mainGroupId,
            childGroup: trackGroupId,
          });
        });
      });
    },
    [dataset, groups]
  );

  const removeIntersectedProxsFromTimeline = useCallback(
    (gondolaGroupId: string) => {
      const mainGroupId = `intersected-proxs-${gondolaGroupId}`;

      const itemsToRemove = dataset.get({
        filter: (item) =>
          item.parentGroup === mainGroupId || item.group === mainGroupId,
      });

      itemsToRemove.forEach((item) => {
        dataset.remove(item.id);
      });

      const groupsToRemove = groups.get({
        filter: (group) =>
          group.id === mainGroupId || group.parentGroup === mainGroupId,
      });

      groupsToRemove.forEach((group) => {
        groups.remove(group.id);
      });
    },
    [dataset, groups]
  );

  const highlightIntersectedProxsTimeline = useCallback(
    (selectedProxId: string | null) => {
      const batchUpdates: Array<{ id: any; color?: string }> = [];

      dataset.forEach((item) => {
        if (!item.id || typeof item.id !== "string") return;

        const itemId = item.id as string;
        const isIntersectedProxItem =
          item.parentGroup?.toString().includes("intersected-proxs") &&
          (itemId.includes("-start") ||
            itemId.includes("-end") ||
            itemId.includes("-range"));

        if (isIntersectedProxItem) {
          const isSelected =
            selectedProxId &&
            (itemId === `${selectedProxId}-start` ||
              itemId === `${selectedProxId}-end` ||
              itemId === `${selectedProxId}-range`);

          batchUpdates.push({
            id: item.id,
            color: isSelected
              ? TIMELINE_ITEM_COLORS.BLUE
              : TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          });
        }
      });

      if (batchUpdates.length > 0) {
        dataset.update(batchUpdates);
      }
    },
    [dataset]
  );

  const highlightIntersectedProxsByMatchScores = useCallback(
    (proxIds: string[], positiveScoreIndices: number[]) => {
      const batchUpdates: Array<{ id: any; color?: string }> = [];

      dataset.forEach((item) => {
        if (!item.id || typeof item.id !== "string") return;

        const itemId = item.id as string;
        const isIntersectedProxItem =
          item.parentGroup?.toString().includes("intersected-proxs") &&
          (itemId.includes("-start") ||
            itemId.includes("-end") ||
            itemId.includes("-range"));

        if (isIntersectedProxItem) {
          const isHighlighted = proxIds.some((proxId, index) => {
            const shouldHighlight = positiveScoreIndices.includes(index);
            return (
              shouldHighlight &&
              (itemId === `${proxId}-start` ||
                itemId === `${proxId}-end` ||
                itemId === `${proxId}-range`)
            );
          });

          batchUpdates.push({
            id: item.id,
            color: isHighlighted
              ? TIMELINE_ITEM_COLORS.BLUE
              : TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          });
        }
      });

      if (batchUpdates.length > 0) {
        dataset.update(batchUpdates);
      }
    },
    [dataset]
  );

  const clearIntersectedProxsHighlighting = useCallback(() => {
    const batchUpdates: Array<{ id: any; color?: string }> = [];

    dataset.forEach((item) => {
      if (!item.id || typeof item.id !== "string") return;

      const itemId = item.id as string;
      const isIntersectedProxItem =
        item.parentGroup?.toString().includes("intersected-proxs") &&
        (itemId.includes("-start") ||
          itemId.includes("-end") ||
          itemId.includes("-range"));

      if (
        isIntersectedProxItem &&
        item.color !== TIMELINE_ITEM_COLORS.MEDIUM_GRAY
      ) {
        batchUpdates.push({
          id: item.id,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
        });
      }
    });

    if (batchUpdates.length > 0) {
      dataset.update(batchUpdates);
    }
  }, [dataset]);

  const clearSelectedProximityFromTimeline = useCallback(() => {
    const selectedProximityGroups = groups.get({
      filter: (group) => group.id.toString().startsWith("selected-proximity-"),
    });

    selectedProximityGroups.forEach((group) => {
      const itemsToRemove = dataset.get({
        filter: (item) => item.group === group.id,
      });

      itemsToRemove.forEach((item) => {
        dataset.remove(item.id);
      });

      groups.remove(group.id);
    });
  }, [dataset, groups]);

  const addSelectedProximityToTimeline = useCallback(
    (
      trackId: number,
      extraData: string,
      avatarBinding?: { animalName: string; color: string }
    ) => {
      try {
        const parsedExtra = JSON.parse(extraData);
        const closestGondolaDistTimes =
          parsedExtra.closest_gondola_dist_times_topk;

        if (
          !closestGondolaDistTimes ||
          !Array.isArray(closestGondolaDistTimes)
        ) {
          return;
        }

        clearSelectedProximityFromTimeline();

        const groupId = `selected-proximity-${trackId}`;

        groups.update({
          id: groupId,
          content: `Selected Proximity ${trackId}`,
          color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
          canHide: false,
          canSave: false,
          order: 2,
          trackId: trackId,
          animalName: avatarBinding?.animalName,
          animalColor: avatarBinding?.color,
        });

        closestGondolaDistTimes.forEach((timeStr: string, index: number) => {
          const timestamp = new Date(timeStr);
          const eventId = `selected-proximity-${trackId}-${timeStr}`;

          dataset.update({
            id: eventId,
            start: timestamp,
            content: `Proximity ${trackId} Event ${index + 1}`,
            view: TIMELINE_ITEM_VIEW.EVENT,
            className: TIMELINE_ITEM_VIEW.EVENT,
            color: TIMELINE_ITEM_COLORS.MEDIUM_GRAY,
            group: groupId,
          });
        });
      } catch (error) {
        console.error("Error parsing proximity extra data:", error);
      }
    },
    [dataset, groups, clearSelectedProximityFromTimeline]
  );

  return {
    dataset,
    groups,
    showSavedOnly,
    setShowSavedOnly,
    setTimelineBackgroundRanges,
    updateTimelineBackgroundRanges,
    addTimelineOrderEvent,
    addTimelineSystemTimeEvent,
    createOrderTimelineGroup,
    addTimelineCustomerJourneyEvent,
    addTimelineCustomerJourneyEvents,
    createJourneyTimelineGroups,
    createPinPredictionTimelineGroups,
    createZonePredictionTimelineGroups,
    addTimelineTransactionEvent,
    addTimelineTrackPersistenceEvent,
    addTimelineJourneyProximityEvent,
    addTimelineProximityTimeRangeEvents,
    addGondolaChangesToTimeline,
    removeGondolaChangesFromTimeline,
    highlightGondolaChangesTimeline,
    addIntersectedProxsToTimeline,
    removeIntersectedProxsFromTimeline,
    highlightIntersectedProxsTimeline,
    highlightIntersectedProxsByMatchScores,
    clearIntersectedProxsHighlighting,
    addSelectedProximityToTimeline,
    clearSelectedProximityFromTimeline,
    removeTimelineAnnotationEvent,
    removeTimelineGroup,
    removeTimelineItemsByGroup,
    toggleTimelineGroup,
    removePinPredictionTimelineGroups,
    removeZonePredictionTimelineGroups,
    toggleSavedItemsVisibility,
    highlightTimelineItem,
    clearTimelineData,
  };
}

// Context creation
const TimelineDataContext = createContext<TimelineDataContext | null>(null);

// Provider component
export function TimelineDataProvider({ children }: PropsWithChildren) {
  const timelineData = useTimelineDataManagement();

  return (
    <TimelineDataContext.Provider value={timelineData}>
      {children}
    </TimelineDataContext.Provider>
  );
}

// Hook for using timeline
export function useTimelineData() {
  const context = useContext(TimelineDataContext);
  if (!context) {
    throw new Error("useTimelineData must be used within a TimelineProvider");
  }
  return context;
}
